import { useEffect, useState, useMemo } from "react";
import { Box, Typography, Button, Snackbar } from "@mui/material";
import useParamValues from "../../utils/useParamValues";
import useTranslationSetup from "../../utils/useTranslationSetup";
// import useWebViewBridge from "../../utils/useWebViewBridge";
import StudentSearchBar from "../../components/StudentSearchBar/StudentSearchBar";
import CohortViewHeader from "../../components/CohortViewHeader/CohortViewHeader";
import StudentListOverlay from "../../components/StudentListOverlay/StudentListOverlay";
import CohortCategoryCard from "../../components/CohortCategoryCard/CohortCategoryCard";
import CohortDetailsContent from "../../components/CohortDetailsContent/CohortDetailsContent";
import CohortBottomDrawer from "../../components/CohortBottomDrawer/CohortBottomDrawer";
import {
  cohortDetailsMap,
  cohortIdMap,
  cohortStaticData,
  normalizeCohortId,
  UrlConstants,
  UNASSIGNED_COHORT_KEY,
} from "../../utils/CommonUtils/constants";
import CohortLevelBarSection from "../../components/CohortLevelBar/CohortLevelBarSection";
import downloadImg from "../../assets/download.svg";
import forwardArrowGreen from "../../assets/forward_arrow_green.svg";
import { SCREENS } from "../../utils/CommonUtils/constants";
import "./CohortMainScreen.css";
import { getPaginatedCohortPages } from "../../utils/helper";
import ClassReportCardPdf from "../classReportCardPdf/ClassReportCardPdf";
import { produceEventLog } from "../../utils/helper";
import { getUserProperties } from "../../utils/helper";
import SubjectTag from "../../components/subjectTag/SubjectTag";

const CohortMainScreen = ({
  students,
  setActiveScreen,
  onComparativeAssessmentClick,
  isComparisonLoading,
}) => {
  const { classNum, section } = useParamValues();
  const [searchResults, setSearchResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslationSetup();
  const [openCategory, setOpenCategory] = useState(null);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [cohortDetailsMapState, setCohortDetailsMap] =
    useState(cohortDetailsMap);
  const [showPdfContent, setShowPdfContent] = useState(false);
  const [pdfSavedSnackbarOpen, setPdfSavedSnackbarOpen] = useState(false);
  const [pdfErrorSnackbarOpen, setPdfErrorSnackbarOpen] = useState(false);
  // const pdfDataUriRef = useRef(null);
  // const openPdfWhenReadyRef = useRef(false);
  // const { sendMessageToReactNative } = useWebViewBridge();
  const userProperties = getUserProperties(useParamValues());

  // const openGeneratedPdf = (dataUri) => {
  //   const base64 = dataUri.split(",")[1];
  //   if (window.ReactNativeWebView) {
  //     sendMessageToReactNative(
  //       JSON.stringify({ type: "open_pdf", data: base64 })
  //     );
  //     return;
  //   }
  //   const byteCharacters = atob(base64);
  //   const byteNumbers = new Uint8Array(byteCharacters.length);
  //   for (let i = 0; i < byteCharacters.length; i++) {
  //     byteNumbers[i] = byteCharacters.charCodeAt(i);
  //   }
  //   const blobUrl = URL.createObjectURL(
  //     new Blob([byteNumbers], { type: "application/pdf" })
  //   );
  //   window.open(blobUrl, "_blank");
  // };

  const handlePdfReady = () => {
    setPdfErrorSnackbarOpen(false);
    setPdfSavedSnackbarOpen(true);
    // pdfDataUriRef.current = dataUri;
    // if (openPdfWhenReadyRef.current) {
    //   openPdfWhenReadyRef.current = false;
    //   openGeneratedPdf(dataUri);
    // }
  };

  const handlePdfError = () => {
    setPdfSavedSnackbarOpen(false);
    setPdfErrorSnackbarOpen(true);
  };

  // const handleOpenPdf = () => {
  //   setPdfSavedSnackbarOpen(false);
  //   if (pdfDataUriRef.current) {
  //     openGeneratedPdf(pdfDataUriRef.current);
  //     return;
  //   }
  //   openPdfWhenReadyRef.current = true;
  // };

  const cohortGroups = useMemo(() => {
    const groups = {
      beeg: [],
      ankuran: [],
      pushpan: [],
      falan: [],
      [UNASSIGNED_COHORT_KEY]: [],
    };

    const baseStudents = searchTerm.trim() ? searchResults : students;

    baseStudents.forEach((student) => {
      const cohortKey =
        cohortIdMap[normalizeCohortId(student.cohort)] || UNASSIGNED_COHORT_KEY;
      groups[cohortKey].push(student);
    });

    return groups;
  }, [students, searchResults, searchTerm]);

  useEffect(() => {
    produceEventLog("results_2b_landing_page_view", {
      ...userProperties,
    });
  }, []);

  useEffect(() => {
    const updatedCohortDetailsMap = { ...cohortDetailsMap };

    Object.keys(cohortGroups).forEach((cohortKey) => {
      updatedCohortDetailsMap[cohortKey] = {
        ...updatedCohortDetailsMap[cohortKey],
        students: cohortGroups[cohortKey],
      };
    });

    setCohortDetailsMap(updatedCohortDetailsMap);

    const updatedCategories = Object.keys(cohortGroups).map((key) => {
      const rawId = Object.keys(cohortIdMap).find(
        (id) => cohortIdMap[id] === key
      );
      const cohortId = normalizeCohortId(rawId);
      return {
        ...cohortStaticData[cohortId],
        id: cohortId,
        count: cohortGroups[key].length,
      };
    });

    setCategories(updatedCategories);
  }, [cohortGroups]);

  const handleLearnMore = (id) => {
    setSelectedCohort(id);
    setIsDrawerOpen(true);
    produceEventLog("result_2b_cohort_know_more_button", {
      ...userProperties,
      cohort_information: id,
    });
  };

  const paginatedPages = getPaginatedCohortPages(students, 20, 25);

  // The level bar and the PDF report only cover the four assigned cohorts.
  const assignedCategories = categories.filter(
    (cat) => cat.className !== UNASSIGNED_COHORT_KEY
  );

  const handleToggle = (id) => {
    const newValue = openCategory === id ? null : id;
    setOpenCategory(newValue);

    if (newValue) {
      produceEventLog("result_2b_cohort_expand_button", {
        ...userProperties,
        cohort_information: id,
      });
    }
  };

  const selectedCat = categories.find((cat) => cat.id === selectedCohort);
  const cohortKey = selectedCat ? cohortIdMap[selectedCat.id] : null;
  const cohortDetails = cohortKey ? cohortDetailsMapState[cohortKey] : null;

  return (
    <Box>
      <Box
        className={`cohort-main-screen-header-wrapper ${searchTerm.trim() ? "dimmed" : ""}`}
      >
        <CohortViewHeader
          onBack={() => {
            sessionStorage.setItem(
              "activeScreen",
              SCREENS.RENDER_INSTRUCTIONS_SCREEN
            );
            setActiveScreen(SCREENS.RENDER_INSTRUCTIONS_SCREEN);
          }}
          showBack
        />
      </Box>
      <Box className="cohort-view-screen-container">
        <Box className="cohort-view-screen-header-row">
          <Typography className="cohort-view-screen-header">
            {t("class_heading", { classNum, section: section.toUpperCase() })}
          </Typography>
          <SubjectTag />
        </Box>
        <Typography className="cohort-view-screen-side-text">
          {t("class_heading_side_text")}
        </Typography>
        <CohortLevelBarSection categories={assignedCategories} />
        {UrlConstants.SHOW_COMPARISON && (
          <Box className="cohort-comparison-button-wrapper">
            <Button
              className="cohort-comparison-button"
              variant="outlined"
              disabled={students.length === 0 || isComparisonLoading}
              endIcon={
                <img
                  src={forwardArrowGreen}
                  alt="Forward Arrow"
                  className="cohort-comparison-button-icon"
                />
              }
              onClick={onComparativeAssessmentClick}
            >
              {t("comparative_assessment")}
            </Button>
          </Box>
        )}
        <StudentSearchBar
          students={students}
          onResults={setSearchResults}
          onSearchTermChange={setSearchTerm}
          searchTerm={searchTerm}
        />

        {searchTerm.trim() && (
          <StudentListOverlay
            results={searchResults}
            searchTerm={searchTerm}
            onCloseList={() => {
              setSearchTerm("");
              setSearchResults([]);
            }}
            cohortIdMap={cohortIdMap}
          />
        )}
        <Typography className="cohorts-header">
          {t("cohort_view_heading")}
        </Typography>
        <Typography className="cohorts-side-header">
          {t("cohort_view_side_text")}
        </Typography>

        {categories.map((cat) => {
          const cohortKey = cohortIdMap[cat.id];
          const cohortDetails = cohortDetailsMapState[cohortKey];
          const isUnassigned = cohortKey === UNASSIGNED_COHORT_KEY;

          return (
            <CohortCategoryCard
              key={cat.id}
              title={cat.title}
              className={cat.className}
              count={cat.count}
              icon={cat.icon}
              isOpen={openCategory === cat.id}
              onToggle={() => handleToggle(cat.id)}
              cardDescription={cat.cardDescription}
              descriptionKey={cat.descriptionKey}
              infoBanner={cat.infoBanner}
            >
              <CohortDetailsContent
                {...cohortDetails}
                onLearnMore={
                  isUnassigned ? undefined : () => handleLearnMore(cat.id)
                }
                className={cat.className}
              />
            </CohortCategoryCard>
          );
        })}

        {selectedCohort && selectedCat && cohortDetails && (
          <CohortBottomDrawer
            open={isDrawerOpen}
            cohort={selectedCat}
            onClose={() => setIsDrawerOpen(false)}
            image={selectedCat.icon}
            title={selectedCat.title}
            gifTextArray={selectedCat.gifTextArray}
            gifText={selectedCat.gifText}
          />
        )}
      </Box>
      <Box className="cohort-screen-sticky-box">
        <Button
          className={`cohort-view-sticky-button ${searchTerm.trim() || students.length === 0 ? "disabled" : ""}`}
          variant="contained"
          startIcon={
            <img
              src={downloadImg}
              alt="Download Icon"
              className="cohort-view-sticky-button-icon"
            />
          }
          disabled={students.length === 0}
          onClick={() => {
            if (!navigator.onLine) {
              setPdfSavedSnackbarOpen(false);
              setPdfErrorSnackbarOpen(true);
              return;
            }
            setPdfErrorSnackbarOpen(false);
            setPdfSavedSnackbarOpen(false);
            setShowPdfContent(true);
            produceEventLog("result_2b_class_result_pdf_download_btn", {
              ...userProperties,
            });
            // pdfDataUriRef.current = null;
            // openPdfWhenReadyRef.current = false;
          }}
        >
          {t("download_pdf")}
        </Button>
      </Box>
      <Snackbar
        className="cohort-pdf-saved-snackbar"
        open={pdfSavedSnackbarOpen}
        autoHideDuration={4000}
        onClose={() => setPdfSavedSnackbarOpen(false)}
        message={t("pdf_saved_successfully")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      {/* Open PDF preview from snackbar — disabled; download uses native download_pdf only
      <Snackbar
        className="cohort-pdf-saved-snackbar"
        open={pdfSavedSnackbarOpen}
        message={t("pdf_saved_successfully")}
        action={
          <Button
            className="cohort-pdf-saved-snackbar-open-btn"
            size="small"
            onClick={handleOpenPdf}
          >
            {t("open")}
          </Button>
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      */}
      <Snackbar
        className="cohort-pdf-saved-snackbar cohort-pdf-error-snackbar"
        open={pdfErrorSnackbarOpen}
        message={t("pdf_save_failed")}
        action={
          <Button
            className="cohort-pdf-saved-snackbar-open-btn cohort-pdf-error-snackbar-btn"
            size="small"
            onClick={() => {
              if (!navigator.onLine) {
                setPdfSavedSnackbarOpen(false);
                setPdfErrorSnackbarOpen(true);
                return;
              }
              setPdfErrorSnackbarOpen(false);
              setPdfSavedSnackbarOpen(false);
              setShowPdfContent(true);
              produceEventLog("result_2b_class_result_pdf_download_btn", {
                ...userProperties,
              });
              // pdfDataUriRef.current = null;
              // openPdfWhenReadyRef.current = false;
            }}
          >
            {t("retry_pdf")}
          </Button>
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      {showPdfContent && (
        <ClassReportCardPdf
          showPdfContent={showPdfContent}
          setShowPdfContent={setShowPdfContent}
          students={students}
          paginatedPages={paginatedPages}
          categories={assignedCategories}
          onPdfReady={handlePdfReady}
          onPdfError={handlePdfError}
        />
      )}
    </Box>
  );
};

export default CohortMainScreen;
